/**
 * Frame-scoped typed event queues.
 *
 * Systems never call each other directly: the ability system publishes a `Hit`,
 * and audio / VFX / HUD each read the same queue independently. Queues are
 * drained once per rendered frame, after Presentation — so every consumer,
 * whether it runs at fixed step or at frame rate, sees the same set of events.
 */
export interface EventType<T> {
  readonly name: string
  readonly __type?: T
}

export function defineEvent<T>(name: string): EventType<T> {
  return { name }
}

export class EventBus {
  private readonly queues = new Map<string, unknown[]>()

  emit<T>(type: EventType<T>, event: T): void {
    let q = this.queues.get(type.name)
    if (!q) {
      q = []
      this.queues.set(type.name, q)
    }
    q.push(event)
  }

  read<T>(type: EventType<T>): readonly T[] {
    return (this.queues.get(type.name) as T[] | undefined) ?? EMPTY
  }

  count<T>(type: EventType<T>): number {
    return this.queues.get(type.name)?.length ?? 0
  }

  /** Called once per rendered frame, after every consumer has run. */
  clear(): void {
    for (const q of this.queues.values()) q.length = 0
  }
}

const EMPTY: never[] = []
